/**
 * Representação visual da assinatura, ARRASTÁVEL pelo signatário (M5a).
 *
 * A lógica é a familiar do GOV.BR: o carimbo aparece sobre o documento e quem assina o leva até
 * onde quer assinar. O que se persiste é **fração da página** (origem topo-esquerda, D-027), não
 * pixel de viewport — por isso zoom, scroll, rotação de tela e tamanho de janela não movem a
 * posição lógica. A conversão e o clamp vivem em `@argink/shared/stamp-geometry`, o mesmo módulo
 * que o contrato e a bancada usam.
 *
 * **Duas escolhas que valem explicação:**
 *
 * 1. *A posição em CSS é `%`, não `px`.* Traduzir fração para pixel exigiria saber o tamanho
 *    corrente da página renderizada, e esse número envelhece: o canvas só re-renderiza quando a
 *    página muda, então um `resize` da janela deixaria o carimbo desenhado num lugar e o
 *    documento em outro. Em `%` o navegador refaz a conta sozinho, e a única fonte de verdade
 *    continua sendo a fração.
 * 2. *O arrasto mede o container na hora* (`getBoundingClientRect`), em vez de confiar num
 *    tamanho capturado antes. Mesmo motivo: o gesto tem que ser correto no tamanho que a página
 *    tem AGORA.
 *
 * **Teclado é caminho de primeira classe, não consolo.** Um alvo que só responde a arrastar
 * exclui quem navega por teclado — e a posição do carimbo é ato do signatário, não enfeite. As
 * setas movem em passo fino, `Shift` em passo grosso, e a posição corrente é anunciada em texto.
 *
 * **Sem `createPortal`** (D-084): o carimbo vive dentro da árvore do documento, onde a contenção
 * de foco já vale. Levar isto para fora reabriria a limitação aceita na promoção do M4-1.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { VisualPlacement } from '@argink/shared';
import {
  describePosition,
  dragToPlacement,
  grabOffset,
  isUsableBox,
  placementAfterKey,
  toCssPercent,
  type GrabOffset,
} from '../lib/stampDrag';

/** Espera antes de gravar, para um arrasto (ou uma seta segurada) virar UMA gravação. */
const COMMIT_DEBOUNCE_MS = 500;

export interface DraggableStampProps {
  /** Posição corrente, em frações da página. */
  value: VisualPlacement;
  /** Movimento em andamento — atualiza a tela, ainda não grava. */
  onChange: (next: VisualPlacement) => void;
  /** Gesto concluído — é aqui que a posição vai para o servidor. */
  onCommit: (next: VisualPlacement) => void;
  /** Texto mostrado dentro do carimbo (o conteúdo real é montado pelo motor na assinatura). */
  label: string;
  /** Congela a interação (durante a gravação, ou quando a janela de edição fechou). */
  disabled?: boolean;
}

export function DraggableStamp({ value, onChange, onCommit, label, disabled = false }: DraggableStampProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dragRef = useRef<({ pointerId: number } & GrabOffset) | null>(null);
  const commitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<VisualPlacement | null>(null);
  const [dragging, setDragging] = useState(false);

  const flushCommit = useCallback(() => {
    if (commitTimerRef.current) {
      clearTimeout(commitTimerRef.current);
      commitTimerRef.current = null;
    }
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (pending) onCommit(pending);
  }, [onCommit]);

  const scheduleCommit = useCallback(
    (next: VisualPlacement) => {
      pendingRef.current = next;
      if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
      commitTimerRef.current = setTimeout(() => {
        commitTimerRef.current = null;
        const pending = pendingRef.current;
        pendingRef.current = null;
        if (pending) onCommit(pending);
      }, COMMIT_DEBOUNCE_MS);
    },
    [onCommit],
  );

  // Um gesto pendente não pode morrer com a desmontagem: se o signatário arrasta e navega de
  // página no mesmo instante, a última posição ainda tem que chegar ao servidor.
  useEffect(() => {
    return () => {
      if (commitTimerRef.current) {
        clearTimeout(commitTimerRef.current);
        commitTimerRef.current = null;
        const pending = pendingRef.current;
        pendingRef.current = null;
        if (pending) onCommit(pending);
      }
    };
  }, [onCommit]);

  const applyMove = useCallback(
    (next: VisualPlacement) => {
      onChange(next);
      scheduleCommit(next);
    },
    [onChange, scheduleCommit],
  );

  function handlePointerDown(event: React.PointerEvent<HTMLButtonElement>): void {
    if (disabled || event.button !== 0) return;
    const container = buttonRef.current?.parentElement;
    if (!container) return;
    const box = container.getBoundingClientRect();
    if (!isUsableBox(box)) return;

    // Guarda a distância entre o dedo/cursor e o canto do carimbo: sem isso o carimbo "salta"
    // para centralizar no ponto tocado assim que o arrasto começa.
    dragRef.current = { pointerId: event.pointerId, ...grabOffset(event, box, value) };
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragging(true);
  }

  function handlePointerMove(event: React.PointerEvent<HTMLButtonElement>): void {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const container = buttonRef.current?.parentElement;
    if (!container) return;
    const box = container.getBoundingClientRect();
    if (!isUsableBox(box)) return;

    applyMove(dragToPlacement(event, box, drag, value));
  }

  function endDrag(event: React.PointerEvent<HTMLButtonElement>): void {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    setDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    flushCommit();
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLButtonElement>): void {
    if (disabled) return;
    const next = placementAfterKey(value, event.key, event.shiftKey);
    if (!next) return;
    // A seta aqui move o carimbo; deixá-la rolar a página ao mesmo tempo tornaria o controle
    // inutilizável no teclado.
    event.preventDefault();
    applyMove(next);
  }

  return (
    <button
      ref={buttonRef}
      type="button"
      className={`stamp-handle${dragging ? ' stamp-handle--dragging' : ''}`}
      style={{
        left: toCssPercent(value.x),
        top: toCssPercent(value.y),
        width: toCssPercent(value.width),
        height: toCssPercent(value.height),
      }}
      disabled={disabled}
      aria-label={`Posição da sua assinatura: ${describePosition(value)}. Arraste, ou use as setas do teclado para mover.`}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onKeyDown={handleKeyDown}
      onBlur={flushCommit}
    >
      <span className="stamp-handle__label" aria-hidden="true">
        {label}
      </span>
    </button>
  );
}
